import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { msalConfig } from './auth/authConfig';
import Layout from './components/Layout';
import EmailDashboard from './modules/email/EmailDashboard';
import HomeDashboard from './modules/home/HomeDashboard';
import LoginPage from './components/LoginPage';

import EmailDetailView from './modules/email/EmailDetailView';
import AuditTrail from './modules/audit/AuditTrail';
import ReportSubscriptions from './modules/reports/ReportSubscriptions';

const msalInstance = new PublicClientApplication(msalConfig);

// Wait for MSAL to fully initialize (handles redirect response)
const msalInitPromise = msalInstance.initialize().then(() => {
  return msalInstance.handleRedirectPromise();
}).then((response) => {
  if (response) {
    msalInstance.setActiveAccount(response.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }
});

// Simple placeholder for future modules
const Placeholder = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-96 text-gray-500">
    <div className="text-lg font-medium">{title}</div>
    <div className="text-sm">Module coming in v1.1</div>
  </div>
);

// Protected app shell — only rendered when authenticated
function AuthenticatedApp() {
  return (
    <Layout>
      <Routes>
        {/* Index Route: Home Dashboard */}
        <Route path="/" element={<HomeDashboard />} />

        {/* Email Route */}
        <Route path="/email" element={<EmailDashboard />} />
        <Route path="/email/:category" element={<EmailDetailView />} />
        
        {/* Audit Trail */}
        <Route path="/audit" element={<AuditTrail />} />

        {/* Report Subscriptions */}
        <Route path="/report-subscriptions" element={<ReportSubscriptions />} />

        {/* Future Routes (Placeholder) */}
        <Route path="/security" element={<Placeholder title="Security Oversight" />} />
        <Route path="/staff" element={<Placeholder title="Staff Access Control" />} />

        {/* Catch-all: Redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    msalInitPromise.then(() => setIsReady(true)).catch((err) => {
      console.error('MSAL init error:', err);
      setIsReady(true); // still render so login page is shown
    });
  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-sm font-medium animate-pulse">Initialising...</div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <AuthenticatedTemplate>
          <AuthenticatedApp />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <LoginPage />
        </UnauthenticatedTemplate>
      </BrowserRouter>
    </MsalProvider>
  );
}

export default App;